<!-- Consider renaming this component to message-thread/ComposePreview -->
<template>
  <div class="w-100">
    <div v-if="hasPreviewContent" :class="`upload-manager d-flex  ${classes}`">
      <div
        v-for="file in uploadedFiles"
        :key="file.signed_id"
        class="upload-manager__preview"
      >
        <v-button
          round
          ghost
          class="upload-manager__remove p-absolute"
          @click="removeUploadedFile(file)"
        >
          <v-icon icon="cancel" />
        </v-button>
        <template v-if="isVideo(file)">
          <video :ref="videoPreviewRef(file.signed_id)">
            <source
              :src="file.preview_url"
              type="video/mp4"
              @error="() => handleFallbackVideo(file)"
            />
            <source
              :src="file.preview_url"
              type="video/ogg"
              @error="() => handleFallbackVideo(file)"
            />
          </video>
          <v-icon
            size="2.75rem"
            icon="play_arrow"
            class="upload-manager__play p-absolute"
          />
        </template>
        <img
          v-else
          :src="file.preview_url"
          @error="e => handleFallbackImage(e.target, file)"
        />
      </div>
      <div
        v-for="attachment in attachments"
        :key="attachment.url"
        :class="[
          'upload-manager__preview --attachment',
          { '--has-image': attachment.image }
        ]"
      >
        <v-button
          round
          ghost
          class="upload-manager__remove p-absolute"
          @click="removeAttachment(attachment)"
        >
          <v-icon icon="cancel" />
        </v-button>
        <collection-item
          is-attachment
          card-display="horizontal"
          card-style="flat"
          :modifiers="`${attachment.image ? '' : 'no-image'}`"
          :title="attachmentTitle(attachment)"
          :content-type="attachmentSubheading(attachment)"
          :to="attachment.url"
          class="attachment-preview__item"
        >
          <template v-slot:image>
            <div class="attachment-preview__image h-100">
              <img :src="attachment.image" class="h-100" />
            </div>
          </template>
        </collection-item>
      </div>
      <div
        v-for="file in uploadingFiles"
        :key="file.key || +new Date()"
        class="upload-manager__preview --uploading"
      >
        <video v-if="isVideo(file)">
          <source :src="blobUrl(file)" type="video/mp4" />
          <source :src="blobUrl(file)" type="video/ogg" />
        </video>
        <img v-else :src="blobUrl(file)" />

        <v-button
          v-if="file.failed"
          round
          ghost
          class="upload-manager__remove p-absolute"
          @click="removeUploadingFile(file)"
        >
          <v-icon icon="cancel" />
        </v-button>

        <template v-if="file.failed">
          <div class="upload-manager__status --fail">
            <v-icon icon="error_outline" />
            {{ $t("components.uploadManager.failed") }}
          </div>
        </template>

        <template v-else>
          <div class="upload-manager__status">
            <v-loader />
            <span v-if="loadingMessages[file.key]">
              {{ loadingMessages[file.key] }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <input
      ref="fileUpload"
      type="file"
      :multiple="multiple"
      :accept="supportedFileTypes"
      class="d-none"
      @input="e => addFiles(e)"
    />
  </div>
</template>

<script>
import * as UpChunk from "@mux/upchunk"
import EventBus, { EVENTS } from "@/lib/event-bus"

import ApiClient from "@/lib/api-client"
import { isPresent } from "@/lib/utils"
import { VIDEO_SIZE_LIMIT, VIDEO_UPLOAD_STATUS } from "@/lib/upload-helpers"
import { SUPPORTED_VIDEO_MIME_TYPES } from "@shared/upload/supported-video-types"
import { generateMuxThumbnailUrl } from "@/lib/video-utils"

import ImageUpload from "@/lib/mixins/image-upload"

export default {
  mixins: [ImageUpload],

  components: {
    CollectionItem: () => import("@/components/CollectionItem")
  },

  props: {
    multiple: { type: Boolean, default: true },
    classes: { type: String, default: null },
    uploadKey: { type: Number, required: true },
    uploadedFiles: { type: Array, default: () => [] },
    attachments: { type: Array, default: () => [] }
  },

  data() {
    return {
      loadingMessages: {},
      uploadingFiles: [],
      supportedFileTypes: "image/*, ".concat(SUPPORTED_VIDEO_MIME_TYPES)
    }
  },

  computed: {
    hasPreviewContent() {
      return this.hasUploadFiles || this.hasAttachments
    },

    hasUploadFiles() {
      return this.uploadedFiles.length > 0 || this.hasUploadingFiles
    },

    hasAttachments() {
      return this.attachments.length > 0
    },

    hasUploadingFiles() {
      return this.uploadingFiles.length > 0
    }
  },

  beforeDestroy() {
    EventBus.$off(EVENTS.OPEN_FILE_DIALOG, this.handleOpenFileDialog)
  },

  mounted() {
    EventBus.$on(EVENTS.OPEN_FILE_DIALOG, this.handleOpenFileDialog)
  },

  watch: {
    hasUploadingFiles: {
      immediate: true,
      handler: function(value) {
        this.$emit("uploading", value)
      }
    }
  },

  methods: {
    generateMuxThumbnailUrl,

    handleFallbackImage(element, file) {
      element.src = file.url
    },

    handleFallbackVideo(file) {
      const el = this.$refs[this.videoPreviewRef(file.signed_id)]

      if (el && el[0]) {
        el[0].poster = generateMuxThumbnailUrl(file.url)
      }
    },

    videoPreviewRef(id) {
      return `video-preview-${id}`
    },

    isVideo(file) {
      return file.type.startsWith("video/")
    },

    attachmentTitle(attachment) {
      return attachment.title ? attachment.title : attachment.url
    },

    attachmentSubheading(attachment) {
      return isPresent(attachment.page_id) && !attachment.is_legacy_type
        ? attachment.type
        : attachment.title
        ? attachment.url
        : ""
    },

    handleOpenFileDialog({ key, supportedFileTypes }) {
      if (key !== this.uploadKey) return
      if (supportedFileTypes) this.supportedFileTypes = supportedFileTypes

      this.$nextTick(() => {
        this.$refs.fileUpload.click()
      })
    },

    async addFiles(e) {
      const files = Object.values(e.target.files)
      let validFiles = []

      this.resetUploadInput()

      // Validate files
      files.forEach((file, index) => {
        // TODO: extract video validation & upload to library
        if (this.isVideo(file)) {
          if (file.size > VIDEO_SIZE_LIMIT) {
            this.showError(
              file,
              this.$t("components.uploadManager.videoTooLarge", {
                size: "50mb"
              })
            )
            return
          }
        } else {
          const validation = this.validateImage(file)

          if (!validation.valid) {
            this.showError(file, validation.errorMessage)
            return
          }
        }
        file.key = +new Date() + index
        validFiles.push(file)
      })

      // Handle upload valid files
      this.uploadingFiles.push(...validFiles)

      for (const file of validFiles) {
        try {
          if (this.isVideo(file)) {
            this.uploadVideo(file)
          } else {
            const uploadedImage = await this.uploadImage(file)

            this.$emit("uploaded", {
              type: "image/",
              url: uploadedImage.path,
              signed_id: uploadedImage.signed_id,
              preview_url: this.blobUrl(file)
            })

            this.removeUploadingFile(file)
          }
        } catch {
          this.markErrorForUploadingFile(file)
        }
      }
    },

    uploadVideo(file) {
      this.$set(
        this.loadingMessages,
        file.key,
        this.$t("components.uploadManager.uploading")
      )

      return ApiClient.post(`/threads/uploads`, {
        filename: file.name
      })
        .then(res => {
          const upload = UpChunk.createUpload({
            endpoint: res.data.presigned_url,
            file: file
          })

          upload.on("success", () =>
            this.ensureVideoPrepared(res.data.upload_id, file)
          )

          upload.on("progress", progress => {
            if (progress.detail === 100) {
              this.$set(
                this.loadingMessages,
                file.key,
                this.$t("components.uploadManager.preparing")
              )
            } else {
              this.$set(
                this.loadingMessages,
                file.key,
                `${Math.round(progress.detail)}%`
              )
            }
          })
        })
        .catch(() => {
          this.markErrorForUploadingFile(file)
        })
    },

    markErrorForUploadingFile(file) {
      let index = this.uploadingFiles.findIndex(f => f == file)
      let fileToUpdate = this.uploadingFiles[index]
      fileToUpdate.failed = true

      this.$set(this.uploadingFiles, index, fileToUpdate)

      if (file.key) {
        this.$set(
          this.loadingMessages,
          file.key,
          this.$t("components.uploadManager.failed")
        )
      }
      this.showError(file)
    },

    ensureVideoPrepared(uploadId, file) {
      try {
        ApiClient.get(`/threads/uploads/${uploadId}`)
          .then(({ data }) => {
            if (isPresent(data.errors)) {
              this.markErrorForUploadingFile(file)
              return
            }

            if (data.status === VIDEO_UPLOAD_STATUS.PREPARING) {
              setTimeout(() => {
                this.ensureVideoPrepared(uploadId, file)
              }, 1000)

              return
            }

            this.$emit("uploaded", {
              type: "video/",
              url: data.video_url,
              signed_id: data.video_id,
              preview_url: this.blobUrl(file)
            })

            this.removeUploadingFile(file)
          })
          .catch(() => {
            this.markErrorForUploadingFile(file)
          })
      } catch (e) {
        this.markErrorForUploadingFile(file)
      }
    },

    showError(
      file,
      message = this.$t("components.uploadManager.couldNotUpload")
    ) {
      this.$notify(`${message}: ${file.name}`)
    },

    removeUploadingFile(file) {
      this.$delete(this.loadingMessages, file.key)
      this.uploadingFiles = this.uploadingFiles.filter(f => f != file)
    },

    removeUploadedFile(file) {
      this.$emit("removeUploaded", file)
    },

    removeAttachment(attachment) {
      this.$emit("removeAttachment", attachment)
    },

    resetUploadInput() {
      this.$refs.fileUpload.value = ""
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixins";

.upload-manager {
  @include hide-scroll-bars;

  padding: 9px 11px 6px;
  overflow-x: scroll;

  &__preview {
    margin-right: 1rem;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;

    &.--uploading {
      img,
      video {
        opacity: 0.5;
      }
    }

    &.--attachment {
      .attachment-preview__item {
        width: 200px;
        border-radius: 6px;
        height: $message-file-upload-preview--height;
        border: 1px solid $color-ui--grey-20;

        .attachment-preview__image {
          flex-grow: 1;

          img {
            width: 100%;
            border-right: 1px solid $color-ui--grey-10;
            border-radius: 6px 0 0 6px;
            object-fit: cover;
          }
        }
      }

      &.--has-image {
        .attachment-preview__item {
          width: 250px;
        }
      }
    }

    &:not(.--attachment) {
      img {
        max-width: 250px;
        min-width: 50px;
      }

      img,
      video {
        height: $message-file-upload-preview--height;
        border-radius: 6px;
        object-fit: cover;
      }
    }
  }

  &__remove {
    top: -0.75rem;
    right: -0.75rem;
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    min-height: 2rem;
    color: $color-ui--black;
    z-index: 10;

    .v-icon {
      background-color: $color-ui--white;
      border-radius: 50%;
    }
  }

  &__play {
    background-color: rgb($color-ui--black, 0.2);
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-ui--white;
  }

  &__status {
    background: $color-ui--white;
    border-radius: $size--2;
    position: absolute;
    left: 2px;
    bottom: 2px;
    height: 18px;
    padding: 0.25rem;
    box-shadow: $box-shadow--small;
    display: flex;
    align-items: center;
    font-size: $font-size--x-small;
    color: $color-ui--grey-70;
    font-weight: $font-weight--semi-bold;

    .v-loader {
      height: 14px;
      width: 14px;
      border-width: 2px;
    }

    span {
      margin-left: 0.25rem;
    }

    &.--fail {
      color: $color-ui--red;

      .v-icon {
        font-size: 1rem;
        margin-left: 0;
        margin-right: 0.25rem;
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/mixins";

.upload-manager__preview.--attachment {
  .collection-item__title {
    @include overflow-ellipsis;
  }
}
</style>
